// src/utils/resultsPipeline.js
/**
 * Layer 2: normalize a raw API offer into a canonical offer
 * @param {object} rawOffer
 * @param {string} userCurrency
 * @param {array} vendors - Pass the vendors array from your API response
 */
const CAR_TYPE_PARENT = {
  // Cars
  B: "Cars",
  C: "Cars",
  D: "Cars",
  L: "Cars",
  E: "Cars",
  S: "Cars",
  T: "Cars",
  N: "Cars",
  Z: "Cars",
  W: "Cars",

  // SUVs
  F: "SUVs",
  G: "SUVs",
  J: "SUVs",
  R: "SUVs",

  // Vans & Trucks
  V: "Vans & Trucks",
  K: "Vans & Trucks",
  M: "Vans & Trucks",

  P: "Other",
  Q: "Other",
  Y: "Other",
  H: "Other",
  X: "Other",
};

export function getParentTypeFromCarType(carType) {
  const key = (carType || "X").toUpperCase();
  return CAR_TYPE_PARENT[key] || "Others";
}



// --- ACRISS (first letter) -> child category (Priceline naming) ---
// src/utils/resultsPipeline.js

const ACRISS_CHILD = {
  M: "Mini",
  E: "Economy",
  C: "Compact",
  I: "Mid-Size",
  S: "Standard",
  F: "Full Size",
  P: "Premium",
  L: "Luxury",
  N: "Mini",
  H: "Economy",
  D: "Compact",
  J: "Intermediate",
  R: "Standard",
  G: "Full Size",
  U: "Premium",
  W: "Luxury",
  O: "Full Size",
};


// --- STANDARD FRONTEND CATEGORY CANON ---
// export const STANDARD_CAR_CATEGORIES = {
//   Cars: [
//     "Mini",
//     "Economy",
//     "Compact",
//     "Mid-Size",
//     "Standard",
//     "Full Size",
//     "Premium",
//     "Luxury",
//   ],

//   SUVs: [
//     "Compact SUV",
//     "Mid-Size SUV",
//     "Full Size SUV",
//     "Premium SUV",
//   ],

//   "Vans & Trucks": [
//     "Van",
//     "Minivan",
//     "Cargo Van",
//     "Pickup Truck",
//   ],

//   Others: [
//     "Convertible",
//     "Supplier's Choice",
//   ],
// };

const VAN_TRUCK_CHILD = {
  V: "Minivan",
  K: "Cargo Van", 
  M: "Mini Van",
};

export function getChildTypeFromCarClass(carClass) {
  const letter = (carClass || "X").toUpperCase().charAt(0);
  return ACRISS_CHILD[letter] || "Other";
}

// Priceline-like child label under SUVs
export function getChildLabel(parent, child, carType) {
  if (parent === "SUVs") {
    return `${child} SUV`;
  }
  if (parent === "Vans & Trucks") {
    return VAN_TRUCK_CHILD[carType?.toUpperCase()] || "Van";
  }
  return child;
}


// Money formatting uses user-selected currency always
export function formatMoney(amount, currencyCode = "USD") {
  if (amount == null) return "";

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "symbol", // 👈 THIS is the key
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getSliderCategory(vehicle) {
  if (vehicle.parentType === "Others") return null; // ✅ important

  if (vehicle.parentType === "Vans & Trucks") return "Van";
  if (vehicle.parentType === "SUVs") return "SUV";

  const child = vehicle.childType;

  if (["Mini", "Economy", "Compact"].includes(child)) return "Small";
  if (["Mid-Size", "Standard"].includes(child)) return "Medium";
  if (["Full Size", "Premium"].includes(child)) return "Large";
  if (child === "Luxury") return "Luxury";

  return null;
}

/**
 * Layer 2: normalize a raw API offer into a canonical offer
 * @param {object} rawOffer
 * @param {string} userCurrency
 */

function isConvertible(carClass) {
  return carClass?.toUpperCase()?.charAt(1) === "T";
}

function isSuppliersChoice(carClass) {
  return carClass?.toUpperCase()?.charAt(0) === "X";
}

export function normalizeOffer(rawOffer, userCurrency, vendors = []) {
  const general = rawOffer?.general || {};
  const payment = rawOffer?.payment || {};

  // ✅ Map snake_case → camelCase explicitly
  const carType = general.car_type;
  const carClass = general.car_class;
  const acriss = general.acriss;

  const baseParent = getParentTypeFromCarType(carType);
  const childBase = getChildTypeFromCarClass(carClass);

  const resolvedChild =
    baseParent === "Vans & Trucks"
      ? VAN_TRUCK_CHILD[carType?.toUpperCase()] || "Van"
      : childBase;

  let parent = baseParent;
  let child = getChildLabel(baseParent, childBase, carType);

  // ✅ OVERRIDES
  if (isConvertible(carClass)) {
    parent = "Others";
    child = "Convertible";
  }

  if (isSuppliersChoice(carClass)) {
    parent = "Others";
    child = "Special";
  }

  const displayCategory = getSliderCategory({
    carType,
    childType: child
  });



  //  Price rule
  const totalPrice =
    Number(payment.total_amount || 0);

  const payNowPrice = Number(payment.prepaid_amount || 0);
  const payAtPickupPrice = Number(payment.pay_at_pickup || 0);

  const vendorInfo = vendors.find(v => v.code === rawOffer.vendor_code);
  const displayName = vendorInfo ? vendorInfo.name : rawOffer.vendor_code;
  const logoUrl = vendorInfo?.logo_url || null;
  const pickupType = vendorInfo?.pickup_location?.pickup_type || null;

  return {
    // identity
    offerId: rawOffer.offer_hash,
    vendorCode: rawOffer.vendor_code,
    productCode: rawOffer.product_code,
    productName: rawOffer.product_name,
    name: displayName,
    logoUrl: logoUrl,
    pickupType,

    // grouping
    model: general.model || "Similar car",
    acriss: (acriss || "XXXX").toUpperCase(),
    carType: carType || "X",
    parentType: parent,
    childType: child,
    displayCategory,

    // specs
    seats: general.seats ?? null,
    bags: general.bags ?? null,
    doors: general.doors ?? null,
    transmission: general.transmission === "A" ? "Automatic" : "Manual",
    airCond: !!general.air_cond,
    imageUrl: general.image_url || "",

    //deposit
    depositAmount: rawOffer.deposit_amount ?? null,

    instantConfirmation: !!rawOffer.instant_confirmation,
    // policies
    fuelPolicy: rawOffer.fuel_policy || null,
    mileageUnlimited: rawOffer?.mileage_info?.is_unlimited ?? null,

    // pricing
    currency: userCurrency || "USD",
    totalPrice,
    payNowPrice,
    payAtPickupPrice,

    inclusions: Array.isArray(rawOffer.inclusions_info)
      ? rawOffer.inclusions_info
      : [],
    raw: rawOffer,
  };
}



/**
 * Layer 3: optionally group normalized offers into vehicle results.
 * Group key = vendorCode + model + acriss
 */
// src/utils/resultsPipeline.js

export function buildVehicleResults(normalizedOffers, groupEnabled = true) {
  if (!groupEnabled) {
    return normalizedOffers.map((o) => ({
      vehicleKey: `${o.vendorCode}|${o.model}|${o.acriss}|${o.offerId}`,
      vendorCode: o.vendorCode,
      name: o.name, // Ensure name is here
      model: o.model,
      acriss: o.acriss,
      parentType: o.parentType,
      childType: o.childType,
      displayCategory: o.displayCategory,
      imageUrl: o.imageUrl,
      transmission: o.transmission,
      seats: o.seats,
      bags: o.bags,
      pickupType: o.pickupType,
      primaryOffer: o,
      offers: [o],
      logoUrl: o.logoUrl,
    }));
  }

  const map = new Map();

  for (const o of normalizedOffers) {
    const key = `${o.vendorCode}|${o.model}|${o.acriss}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        vehicleKey: key,
        vendorCode: o.vendorCode,
        name: o.name, // 👈 ADD THIS LINE: This makes the name visible to the FilterMenu
        model: o.model,
        acriss: o.acriss,
        parentType: o.parentType,
        childType: o.childType,
        displayCategory: o.displayCategory,
        imageUrl: o.imageUrl,
        transmission: o.transmission,
        seats: o.seats,
        bags: o.bags,
        pickupType: o.pickupType,
        offers: [o],
        primaryOffer: o,
        logoUrl: o.logoUrl,
      });
    } else {
      existing.offers.push(o);
    }
  }

  // Final pass to choose the cheapest primary offer
  for (const v of map.values()) {
    v.primaryOffer = [...v.offers].sort((a, b) => a.totalPrice - b.totalPrice)[0];
    // Sync the top-level name with the primary offer's name
    v.name = v.primaryOffer.name;
    v.logoUrl = v.primaryOffer.logoUrl;
    v.pickupType = v.primaryOffer.pickupType;
  }

  return Array.from(map.values());
}


export function buildCarTypeFilterIndex(vehicleResults) {
  const index = {};

  vehicleResults.forEach((v) => {
    const parent = v.parentType;
    const child = v.childType;

    if (!parent || !child) return;

    if (!index[parent]) {
      index[parent] = { count: 0, sub: {} };
    }

    index[parent].count += 1;

    if (!index[parent].sub[child]) {
      index[parent].sub[child] = 0;
    }

    index[parent].sub[child] += 1;
  });

  return index;
}


export const DEPOSIT_BUCKETS = [
  "Zero Deposit",
  "1 - 200",
  "201 - 500",
  "501 - 1500",
  "1501+",
];

export function getDepositBucket(amount) {
  if (amount == null ) return null;
  if(amount === 0) return "Zero Deposit";

  if (amount >= 1 && amount <= 200) return "1 - 200";
  if (amount >= 201 && amount <= 500) return "201 - 500";
  if (amount >= 501 && amount <= 1500) return "501 - 1500";
  if (amount >= 1501) return "1501+";

  return null;
}



export function getTerminalLabel(pickupType) {
  return pickupType;
}

export function applyFilters(vehicleResults, selectedFilters) {
  const has = (k) => Array.isArray(selectedFilters?.[k]) && selectedFilters[k].length > 0;
  

  return vehicleResults.filter((v) => {
    // Car Type (parent or child)
    if (has("Car Type")) {
      const selected = selectedFilters["Car Type"];
      const parentMatch = selected.includes(v.parentType);
      const childMatch = selected.includes(`${v.parentType}:${v.childType}`);
      if (!parentMatch && !childMatch) return false;
    }

    // Transmission
    if (has("Transmission")) {
      if (!selectedFilters["Transmission"].includes(v.transmission)) return false;
    }

    // Supplier (use vendorCode for now; can switch to vendor.name later)
    if (has("Supplier")) {
      if (!selectedFilters["Supplier"].includes(v.name)) return false;
    }

    // Miles (based on primary offer only)
    if (has("Miles")) {
      const m = v.primaryOffer.mileageUnlimited;
      const label = m === true ? "Unlimited" : "Limited";
      if (!selectedFilters["Miles"].includes(label)) return false;
    }

    // Deposit
    if (has("Deposit")) {
      const deposit = v.primaryOffer.depositAmount;
      const bucket = getDepositBucket(deposit);

      if (!selectedFilters["Deposit"].includes(bucket)) return false;
    }

    // Terminal Type
    if (has("Terminal Type")) {
      const label = getTerminalLabel(v.pickupType);
      if (!selectedFilters["Terminal Type"].includes(label)) return false;
    }

    return true;
  });
}

/**
 * Sort vehicle results.
 * sortBy:
 * - "best-value" (currently cheapest total)
 * - "lowest-price"
 */
export function sortVehicleResults(vehicleResults, sortBy = "best-value") {
  const arr = [...vehicleResults];

  if (sortBy === "lowest-price" || sortBy === "best-value") {
    arr.sort((a, b) => (a.primaryOffer.totalPrice - b.primaryOffer.totalPrice));
    return arr;
  }

  return arr;
}

/**
 * Compute "From price" per filter bucket using primaryOffer.totalPrice.
 */
export function getFromPrice(vehicleResults, currencyCode) {
  const min = vehicleResults.reduce((acc, v) => Math.min(acc, v.primaryOffer.totalPrice), Infinity);
  return Number.isFinite(min) ? formatMoney(min, currencyCode) : null;
}
